import { useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import { isTestMode } from '../../lib/test-mode'

export default function AuthPage() {
  const { signIn, signUp } = useAuth()
  const navigate = useNavigate()
  const [isSignUp, setIsSignUp] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [inviteToken, setInviteToken] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [confirmationSent, setConfirmationSent] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      if (isSignUp) {
        if (password !== confirmPassword) {
          setError('Passwords do not match')
          setLoading(false)
          return
        }
        const { error: err } = await signUp(email, password, inviteToken)
        if (err) {
          setError(err.message)
        } else {
          navigate('/')
        }
      } else {
        const { error: err } = await signIn(email, password)
        if (err) {
          setError(err.message)
        } else {
          navigate('/')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#030712] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-mono text-xs text-cyan-500/60 tracking-[0.3em] uppercase mb-2">
            GCODER
          </h1>
          <p className="font-mono text-sm text-slate-500">
            {isSignUp ? 'Create your account' : 'Sign in to continue'}
          </p>
        </div>

        {confirmationSent ? (
          <div className="border border-cyan-500/20 rounded-lg p-6 bg-slate-900/50">
            <p className="font-mono text-sm text-cyan-400 mb-2">Check your email</p>
            <p className="font-mono text-xs text-slate-400">
              We sent a confirmation link to <span className="text-slate-300">{email}</span>.
              Click it to activate your account, then sign in.
            </p>
            <button
              onClick={() => { setConfirmationSent(false); setIsSignUp(false) }}
              className="mt-4 font-mono text-xs text-cyan-500 hover:text-cyan-400 transition-colors"
            >
              Back to sign in
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {isSignUp && !isTestMode && (
              <div>
                <label className="block font-mono text-xs text-slate-500 mb-1.5">Invite Code</label>
                <input
                  type="text"
                  value={inviteToken}
                  onChange={e => setInviteToken(e.target.value)}
                  required
                  autoFocus
                  className="w-full bg-slate-900/80 border border-slate-700/50 rounded-md px-3 py-2 font-mono text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-colors"
                  placeholder="Enter your invite code"
                />
              </div>
            )}

            <div>
              <label className="block font-mono text-xs text-slate-500 mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoFocus={!isSignUp}
                autoComplete="email"
                className="w-full bg-slate-900/80 border border-slate-700/50 rounded-md px-3 py-2 font-mono text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-colors"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label className="block font-mono text-xs text-slate-500 mb-1.5">Password</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                minLength={6}
                autoComplete={isSignUp ? 'new-password' : 'current-password'}
                className="w-full bg-slate-900/80 border border-slate-700/50 rounded-md px-3 py-2 font-mono text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-colors"
                placeholder="Min 6 characters"
              />
            </div>

            {isSignUp && (
              <div>
                <label className="block font-mono text-xs text-slate-500 mb-1.5">Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full bg-slate-900/80 border border-slate-700/50 rounded-md px-3 py-2 font-mono text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-colors"
                  placeholder="Re-enter password"
                />
              </div>
            )}

            {error && (
              <p className="font-mono text-xs text-red-400">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 font-mono text-sm py-2 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Loading...' : isSignUp ? 'Create Account' : 'Sign In'}
            </button>

            {isSignUp && (
              <p className="text-center font-mono text-[11px] text-slate-600 leading-relaxed">
                By creating an account, you agree to our{' '}
                <a href="/terms" target="_blank" className="text-cyan-500/70 hover:text-cyan-400 transition-colors">Terms of Use</a>
                {' '}and{' '}
                <a href="/privacy" target="_blank" className="text-cyan-500/70 hover:text-cyan-400 transition-colors">Privacy Policy</a>.
              </p>
            )}

            <p className="text-center font-mono text-xs text-slate-500">
              {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
              <button
                type="button"
                onClick={() => { setIsSignUp(!isSignUp); setError(null); setConfirmPassword('') }}
                className="text-cyan-500 hover:text-cyan-400 transition-colors"
              >
                {isSignUp ? 'Sign in' : 'Sign up'}
              </button>
            </p>
          </form>
        )}
      </div>
    </div>
  )
}
